import { FloorPlanCard, Grid, Header, LayoutContainer } from '@greystar/designsystem';
import {
  calculateTMLP,
  formatCurrency,
  formatPriceDisplay,
  getAllFees,
  getValidAvailableDate,
  getWindowLocation,
  useAnalytics,
} from '@greystar/lib';
import { getPdpUrlById } from '@greystar/lib/src/helpers/getPdpUrlById';
import {
  AvailableUnitExtended,
  GroupedFloorPlan,
  Price,
  Property,
  UnitCardProps,
} from '@greystar/lib/src/types/property-types';
import { ResponsiveImage } from '@greystar/ui';
import { Placeholder, useSitecoreContext, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import { Suspense, useEffect, useMemo, useState, JSX } from 'react';
import { PropertyFloorPlansProps } from '../../../component-props';
import FloorPlanFilters from '../../FloorPlanFilters/FloorPlanFilters';
import { NoFloorPlans } from './NoFloorPlans';
import { useRouter } from 'next/router';

dayjs.extend(advancedFormat);
dayjs.extend(utc);

interface PropertyFloorPlansPropsExtended extends PropertyFloorPlansProps {
  fields: {
    noResultLabel: Field<string>;
    filterAdjustLabel: Field<string>;
  };
}

interface RoomInfo {
  key: string;
  price: {
    baseprice: string;
    leaseLength: string;
    tmlp: string;
  };
  date: {
    leaseterm: string;
    availabledate: string;
    availabledatetext: string;
  };
  unit: UnitCardProps;
}

const removePastDates = (prices: Price[]): Price[] => {
  const today = new Date();
  const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

  return prices.filter((price) => {
    const leaseStartDate = new Date(price.leaseStartsOn);
    const startUTC = Date.UTC(
      leaseStartDate.getUTCFullYear(),
      leaseStartDate.getUTCMonth(),
      leaseStartDate.getUTCDate()
    );
    return startUTC >= todayUTC;
  });
};

const groupByFloorPlanId = (units: AvailableUnitExtended[]): GroupedFloorPlan[] => {
  const groupedMap = units.reduce((acc, unit) => {
    const { floorPlanId } = unit;
    if (!acc.has(floorPlanId)) {
      acc.set(floorPlanId, {
        floorplan: unit.floorplan,
        units: [],
      });
    }

    //Remove past dates from prices array
    const unitWithFilteredPrices = {
      ...unit,
      prices: unit.isFlatPricing ? [] : removePastDates(unit.prices),
    };
    acc.get(floorPlanId)!.units.push(unitWithFilteredPrices);
    return acc;
  }, new Map<string, GroupedFloorPlan>());

  return Array.from(groupedMap.values()).sort((f1, f2) => {
    const f1Min = Math.min(...f1.units.map((u) => u.minPrice ?? 0));
    const f2Min = Math.min(...f2.units.map((u) => u.minPrice ?? 0));
    return f1Min - f2Min;
  });
};

export const PropertyFloorPlans = (props: PropertyFloorPlansPropsExtended): JSX.Element => {
  const sitecoreContext = useSitecoreContext();
  const [clearTriggerId, setClearTriggerId] = useState(0);
  const [isNoResults, setIsNoResults] = useState(false);
  const property = sitecoreContext.sitecoreContext.property as Property;
  const analytics = useAnalytics();
  const router = useRouter();

  const groupedFloorPlans = useMemo(
    () => groupByFloorPlanId(props.availableUnits),
    [props.availableUnits]
  );

  const [filteredFloorPlans, setFilteredFloorPlans] = useState<GroupedFloorPlan[] | null>(null);
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});

  const toggleGroup = (id: string, floorPlanName: string) => {
    const isCurrentlyOpen = openGroups[id] ?? false;

    setOpenGroups((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));

    const eventAction = isCurrentlyOpen ? 'hide' : 'show';
    const address = `${props.propertyLocation?.address}, ${props.propertyLocation?.city}, ${props.propertyLocation?.state}, ${props.propertyLocation?.postalCode}`;
    const eventData = {
      event: `floor_plan_unit_${eventAction}`,
      action_type: `floor plan unit - ${eventAction}`,
      state: props.propertyLocation?.state,
      property_location: address,
      property_id: props.propertyId ?? '',
      property_name: props.propertyName,
      floor_plan_name: floorPlanName,
      floor_plan_id: id,
      unit_no: '',
      module_name: 'floor plan',
      inpage_location: 'middle',
      click_text: eventAction === 'show' ? `Show` : `Hide `,
      click_type: 'internal',
      destination_url: getWindowLocation(),
    };
    analytics.trackEvent(`floor_plan_unit_${eventAction}`, eventData);
  };

  const mapUnitToRoom = (unit: AvailableUnitExtended, isFlatPricing: boolean) => {
    if (!unit || (unit.prices.length < 1 && !unit.isFlatPricing)) return null;
    const isAvailableToday = dayjs.utc(unit?.availableOn).isBefore(dayjs.utc());
    const price = unit.minPrice;
    const unitPrice = formatCurrency(price, '$');

    const tmlp = calculateTMLP(
      price ?? 0,
      getAllFees(property.fees ?? []),
      1,
      getValidAvailableDate(unit.prices?.[0]?.leaseStartsOn)
    );

    const tmlpDisplay = formatPriceDisplay(tmlp.amount, tmlp.maxAmount, false, 0, true);
    const leaseTerm = isFlatPricing ? '12' : unit?.minBaseRentLeaseTerm;

    return {
      key: unit.unitId,
      price: {
        baseprice: unitPrice,
        leaseLength: `${leaseTerm} month lease`,
        tmlp: tmlpDisplay,
      },
      date: {
        leaseterm: leaseTerm,
        availabledate: isAvailableToday ? 'Now' : dayjs(unit?.availableOn).format('MMMM Do, YYYY'),
        availabledatetext: 'Available',
      },
      unit: unitLink({ unit, leaseTerm: `${leaseTerm}` }),
    };
  };

  const getFloorPlanName = (unitId: string, floorPlans: AvailableUnitExtended[]): string => {
    const matchingUnit = floorPlans.find((unit) => unit.unitId === unitId);
    if (!matchingUnit) {
      return 'Unknown';
    }

    const { floorplan, area } = matchingUnit;
    const bedroomCount =
      floorplan.bedroomCount > 0 ? `${floorplan.bedroomCount} Bedroom` : 'Studio';
    const label = floorplan.label;
    const areaText = area ? `${area} sq. ft.` : '';

    return `${bedroomCount} - ${label} ${areaText}`.trim();
  };

  const unitLink = ({
    unit,
    leaseTerm,
  }: {
    unit: AvailableUnitExtended;
    leaseTerm: string;
  }): UnitCardProps => {
    const today = dayjs.utc();
    const moveInDate = dayjs.utc(unit.availableOn);
    const date = (moveInDate.isBefore(today) ? today : moveInDate).format('YYYY-MM-DD');
    const title = `Unit ${unit.unitNumber}`;
    const propertyUrl = getPdpUrlById(
      props.propertyId,
      props.propertyName,
      props.propertyLocation?.city,
      props.propertyLocation?.state
    );
    const href = `${propertyUrl}/calculator?unitId=${unit.unitId}&leaseTerm=${
      leaseTerm ?? '12'
    }&moveInDate=${date}#fee-calculator`;

    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      const floorPlanName = getFloorPlanName(unit.unitId, props?.availableUnits);
      const address = `${props.propertyLocation?.address}, ${props.propertyLocation?.city}, ${props.propertyLocation?.state}, ${props.propertyLocation?.postalCode}`;
      const eventData = {
        event: 'unit_rent_calculator_entry',
        action_type: 'entry',
        state: props.propertyLocation?.state,
        property_location: address,
        property_id: props.propertyId ?? '',
        property_name: props.propertyName,
        floor_plan_name: floorPlanName,
        floor_plan_id: unit.floorPlanId,
        unit_no: unit.unitNumber,
        module_name: 'floor plan',
        inpage_location: 'middle',
        click_text: title,
        click_type: 'internal',
        destination_url: href,
      };
      await analytics.trackEvent('unit_rent_calculator_entry', eventData);

      router.push(href);
    };

    return {
      title: `Unit ${unit.unitNumber}`,
      href: href,
      onClick: handleClick as () => Promise<void>,
    };
  };

  const handleExternalClear = () => {
    setClearTriggerId(Date.now()); // unique ID
  };

  useEffect(() => {
    const count = filteredFloorPlans?.reduce((acc, group) => acc + group.units.length, 0) ?? 0;
    setIsNoResults(count === 0);
  }, [filteredFloorPlans]);

  return (
    <LayoutContainer flexDirection="column" gap="md" id="fpe-floor-plans">
      <LayoutContainer flexDirection="column" gap="md" id="fpe-floor-plans">
        <Header component={'h2'} color={'primary'}>
          {props.title}
        </Header>
        <FloorPlanFilters
          setFilteredFloorPlans={setFilteredFloorPlans}
          groupedFloorPlans={groupedFloorPlans}
          clearFiltersTrigger={clearTriggerId}
          filterLabel={props.filterLabel}
        />
      </LayoutContainer>
      <Grid gap={'xlg'}>
        <Grid.Item smColumnSpan={4} mdColumnSpan={8} lgColumnSpan={8}>
          <LayoutContainer flexDirection="column" gap="md" id="fpe-floor-plans">
            <Placeholder
              name={`floorplans-above-listing-${props.params.DynamicPlaceholderId}`}
              rendering={props.rendering}
            />
            {isNoResults && (
              <NoFloorPlans
                clearFilters={handleExternalClear}
                noResultLabel={props?.fields?.noResultLabel?.value}
                filterAdjustLabel={props?.fields?.filterAdjustLabel?.value}
              />
            )}

            {filteredFloorPlans?.map((group) => {
              const { floorplan } = group;
              const isFlatPricing = group.units.some((unit) => unit.isFlatPricing);
              const isOpen = openGroups[floorplan.id] || false;
              const area = Math.min(
                ...group.units.filter((u) => !isNaN(Number(u.area))).map((u) => Number(u.area))
              );

              const firstUnit = group?.units?.[0];
              const defaultRoom = mapUnitToRoom(firstUnit, isFlatPricing);
              const availableRooms = group?.units
                ?.slice(1)
                .map((unit) => mapUnitToRoom(unit, isFlatPricing));
              const units = group.units.toSorted((u1, u2) => u1.minPrice - u2.maxPrice);
              const minFloorPlanBaseRent = Math.min(...units.map((u) => u.minPrice));
              const maxFloorPlanBaseRent = Math.max(...units.map((u) => u.maxPrice));
              const floorPlanBaseRent = formatCurrency(minFloorPlanBaseRent, '$');

              const floorPlanMinTmlp = calculateTMLP(
                minFloorPlanBaseRent ?? 0,
                getAllFees(property.fees ?? []),
                1,
                getValidAvailableDate(firstUnit?.availableOn)
              );

              const floorPlanMaxTmlp = calculateTMLP(
                maxFloorPlanBaseRent ?? 0,
                getAllFees(property.fees ?? []),
                1,
                getValidAvailableDate(firstUnit?.availableOn)
              );

              const floorPlanTmlp = formatPriceDisplay(
                floorPlanMinTmlp.amount,
                floorPlanMinTmlp.amount === floorPlanMaxTmlp.amount
                  ? null
                  : floorPlanMaxTmlp.amount,
                false,
                0,
                true
              );

              return defaultRoom ? (
                <FloorPlanCard
                  key={floorplan.id}
                  defaultRoom={defaultRoom}
                  hideText={isOpen ? 'Hide' : 'Show More'}
                  toggleList={() => toggleGroup(floorplan.id, `${floorplan.label}`)}
                  title={`${floorplan.label}`}
                  rooms={[
                    {
                      icon: 'Bedroom',
                      number: floorplan.bedroomCount === 0 ? 'Studio' : floorplan.bedroomCount,
                    },
                    { icon: 'Bathroom', number: floorplan.bathroomCount },
                  ]}
                  area={{
                    area: `${area}`,
                    areaunit: 'sq. ft.',
                  }}
                  image={
                    <Suspense fallback={<div>Loading image…</div>}>
                      <ResponsiveImage
                        image={floorplan.imageUrl}
                        fill={true}
                        width={300}
                        height={200}
                        sizes="88px"
                      />
                    </Suspense>
                  }
                  availableNumberCount={group?.units?.length}
                  availableRooms={
                    availableRooms?.filter((room): room is RoomInfo => room !== null) ?? []
                  }
                  isOpen={isOpen}
                  baseRent={floorPlanBaseRent}
                  tmlp={floorPlanTmlp}
                />
              ) : null;
            })}
          </LayoutContainer>
        </Grid.Item>
        <Grid.Item smColumnSpan={4} mdColumnSpan={8} lgColumnSpan={4}>
          <Placeholder
            name={`pdp-floor-plans-right-${props.params.DynamicPlaceholderId}`}
            rendering={props.rendering}
          />
        </Grid.Item>
      </Grid>
    </LayoutContainer>
  );
};
